/**
 * pj-wrapper.js - Price Judge Wrapper Rule Switcher
 *
 * Validates a full ?discount= code through a lightweight app-proxy endpoint,
 * stores the matching wrapper rule for the current browser session, syncs the
 * rule key into the hidden cart attribute (__pj_wrapper), and toggles the
 * pre-rendered wrapper price/badge markup on collection, PDP, and quick-buy UIs.
 *
 * No external dependencies. Vanilla JS only.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'pj_wrapper_rule';
  var SYNC_KEY = 'pj_wrapper_cart_sync';
  var CODE_KEY = 'pj_wrapper_code';
  var CART_ATTR = '__pj_wrapper';
  var BODY_ATTR = 'data-pj-wrapper';
  var VALIDATE_URL = window.PJ_WRAPPER_VALIDATE_URL || '/apps/price-judge/validate';

  function getStorage() {
    try {
      if (window.localStorage) return window.localStorage;
    } catch (error) {
      // Fall through to sessionStorage.
    }

    try {
      if (window.sessionStorage) return window.sessionStorage;
    } catch (error) {
      // No-op.
    }

    return null;
  }

  var storage = getStorage();

  function matchWrapper(code) {
    var wrappers = window.PJ_WRAPPERS;
    if (!Array.isArray(wrappers) || !code) return null;

    var upper = code.toUpperCase();
    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      if (!wrapper.code) continue;

      var wrapperCode = String(wrapper.code).toUpperCase();
      var isMatch = wrapper.match_mode === 'exact'
        ? upper === wrapperCode
        : upper.indexOf(wrapperCode) === 0;

      if (isMatch) return wrapper;
    }

    return null;
  }

  function readStoredWrapper() {
    if (!storage) return null;

    var stored = storage.getItem(STORAGE_KEY);
    if (!stored) return null;

    try {
      var parsed = JSON.parse(stored);
      if (!parsed || !parsed.rule_key) return null;

      var stillValid = (window.PJ_WRAPPERS || []).some(function (wrapper) {
        return wrapper.rule_key === parsed.rule_key;
      });

      return stillValid ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function readStoredCode() {
    if (!storage) return '';

    var storedCode = storage.getItem(CODE_KEY) || '';
    return storedCode.trim();
  }

  function writeStoredWrapper(activeWrapper, code) {
    if (!storage) return;

    storage.setItem(STORAGE_KEY, JSON.stringify(activeWrapper));
    storage.setItem(CODE_KEY, code);
  }

  function syncCartAttribute(ruleKey) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { [CART_ATTR]: ruleKey || '' } }),
    });
  }

  function clearWrapper() {
    if (storage) {
      storage.removeItem(STORAGE_KEY);
      storage.removeItem(SYNC_KEY);
      storage.removeItem(CODE_KEY);
    }

    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(SYNC_KEY);
      sessionStorage.removeItem(CODE_KEY);
    } catch (error) {
      // No-op.
    }

    if (document.body) {
      document.body.removeAttribute(BODY_ATTR);
    }

    return syncCartAttribute('').catch(function () {
      // Non-critical: a later cart-aware render can recover.
    });
  }

  function isPostPurchaseReturnReferrer() {
    var referrer = document.referrer || '';
    return /\/orders\//i.test(referrer) ||
      /\/thank_you/i.test(referrer) ||
      /\/order_status/i.test(referrer);
  }

  function validateDiscountCode(code) {
    var url = VALIDATE_URL + '?discount=' + encodeURIComponent(code);

    return fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () { return null; })
          .then(function (payload) {
            if (!response.ok) return payload || { valid: false };
            return payload;
          });
      })
      .catch(function () {
        return null;
      });
  }

  function applyWrapperPrices(activeWrapper, root) {
    var spans = (root || document).querySelectorAll('.pj-wrapper-price[data-pj-wrapper]');

    spans.forEach(function (span) {
      if (span.getAttribute('data-pj-wrapper') !== activeWrapper.rule_key) return;

      span.style.display = 'flex';
      span.removeAttribute('aria-hidden');

      var container = span.closest('.price__container');
      if (container) {
        var regular = container.querySelector('.price__regular');
        if (regular) regular.style.display = 'none';

        var existingSale = container.querySelector('.price__sale:not(.pj-wrapper-price)');
        if (existingSale) existingSale.style.display = 'none';
      }

      var pdpBlock = span.closest('.price-judge-price');
      if (pdpBlock) {
        var pdpRegular = pdpBlock.querySelector('.pj-pdp-regular');
        if (pdpRegular) pdpRegular.style.display = 'none';
      }

      var quickBuyRow = span.closest('.pj-qb__price-row');
      if (quickBuyRow) {
        var quickBuyRegular = quickBuyRow.querySelector('.pj-qb__price--regular');
        if (quickBuyRegular) quickBuyRegular.style.display = 'none';

        var quickBuyCompare = quickBuyRow.querySelector('.pj-qb__compare:not([data-pj-wrapper])');
        if (quickBuyCompare) quickBuyCompare.style.display = 'none';
      }
    });
  }

  function applyWrapperBadges(activeWrapper, root) {
    var scope = root || document;
    var wrapperBadges = scope.querySelectorAll('.pj-wrapper-badge[data-pj-wrapper]');

    wrapperBadges.forEach(function (badgeWrap) {
      var parent = badgeWrap.parentElement;
      if (!parent) return;

      parent.querySelectorAll('.pj-wrapper-badge[data-pj-wrapper]').forEach(function (candidate) {
        candidate.style.display = 'none';
        candidate.setAttribute('aria-hidden', 'true');
      });

      parent.querySelectorAll('.pj-ov-wrap--default').forEach(function (candidate) {
        candidate.style.display = 'none';
        candidate.setAttribute('aria-hidden', 'true');
      });

      if (badgeWrap.getAttribute('data-pj-wrapper') === activeWrapper.rule_key) {
        badgeWrap.style.display = 'flex';
        badgeWrap.removeAttribute('aria-hidden');
      }
    });
  }

  function attachLiveObservers(activeWrapper) {
    document.addEventListener('variant:change', function () {
      setTimeout(function () {
        applyWrapperPrices(activeWrapper, document);
        applyWrapperBadges(activeWrapper, document);
      }, 50);
    });

    if (typeof window.subscribe === 'function') {
      window.subscribe('variant:change', function () {
        setTimeout(function () {
          applyWrapperPrices(activeWrapper, document);
          applyWrapperBadges(activeWrapper, document);
        }, 50);
      });
    }

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;

          if (node.matches && node.matches('.pj-wrapper-price[data-pj-wrapper]')) {
            applyWrapperPrices(activeWrapper, node.parentElement || document);
            applyWrapperBadges(activeWrapper, node.parentElement || document);
          } else {
            applyWrapperPrices(activeWrapper, node);
            applyWrapperBadges(activeWrapper, node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  async function init() {
    var params = new URLSearchParams(window.location.search);
    var hasDiscountParam = params.has('discount');
    var rawUrlCode = hasDiscountParam ? (params.get('discount') || '').trim() : '';
    var wantsClear = hasDiscountParam && (!rawUrlCode || /^(clear|none|off)$/i.test(rawUrlCode));

    if (wantsClear) {
      await clearWrapper();
      return;
    }

    if (!hasDiscountParam && isPostPurchaseReturnReferrer()) {
      await clearWrapper();
      return;
    }

    var activeWrapper = null;
    var activeCode = '';

    if (hasDiscountParam) {
      var localMatch = matchWrapper(rawUrlCode);
      if (!localMatch) {
        await clearWrapper();
        return;
      }

      var validation = await validateDiscountCode(rawUrlCode);
      if (!validation || !validation.valid || validation.rule_key !== localMatch.rule_key) {
        await clearWrapper();
        return;
      }

      activeWrapper = localMatch;
      activeCode = rawUrlCode;
      writeStoredWrapper(activeWrapper, activeCode);
    } else {
      activeCode = readStoredCode();
      activeWrapper = readStoredWrapper();

      if (!activeCode || !activeWrapper) {
        await clearWrapper();
        return;
      }

      var storedMatch = matchWrapper(activeCode);
      if (!storedMatch || storedMatch.rule_key !== activeWrapper.rule_key) {
        await clearWrapper();
        return;
      }

      var storedValidation = await validateDiscountCode(activeCode);
      if (!storedValidation || !storedValidation.valid || storedValidation.rule_key !== storedMatch.rule_key) {
        await clearWrapper();
        return;
      }

      activeWrapper = storedMatch;
      writeStoredWrapper(activeWrapper, activeCode);
    }

    var syncedRuleKey = storage ? storage.getItem(SYNC_KEY) : null;
    if (hasDiscountParam || syncedRuleKey !== activeWrapper.rule_key) {
      syncCartAttribute(activeWrapper.rule_key)
        .then(function () {
          if (storage) {
            storage.setItem(SYNC_KEY, activeWrapper.rule_key);
          }
        })
        .catch(function () {
          // Non-critical: storefront visuals still work and the next page can retry.
        });
    }

    document.body.setAttribute(BODY_ATTR, activeWrapper.rule_key);
    applyWrapperPrices(activeWrapper, document);
    applyWrapperBadges(activeWrapper, document);
    attachLiveObservers(activeWrapper);
  }

  init().catch(function () {
    // Fail closed: if validation or sync breaks, do not leave stale wrapper UI hanging around.
    clearWrapper();
  });
})();
